import { takeLatest, select, put, all, call, cancelled } from 'redux-saga/effects';
import axios from 'axios';
import { set } from 'lodash';
import http from '@api/http';
import getCompareFilterData from '@api/comparison';
import { clearCompareZoomData } from '@store/comparison/actions';
import { currentMetricsMetaSelector } from '@store/common/selectors';
import * as commonTypes from '@store/common/types';
import { selector as commonSelector } from '@store/common';
import { NO_COMPARE_MODE } from '@utils/constants';
import { granularity } from '@utils';
import * as types from './types';
import { sagas as fetchConfig } from './config';
import updateConfigSagas from './updateConfigSagas';
import sharingSelector from './sharing/selectors';
import { parseQuery, parseCompareData, parseCompareTotals } from './utils';

const api = {};

function* fetchCompareZoomData(config) {
  const { interval, zoomInterval } = config;
  const mainGranularity = granularity(interval.from, interval.to, true);
  const zoomGranularity = granularity(zoomInterval.from, zoomInterval.to, true);
  const currentMetricsMeta = yield select(currentMetricsMetaSelector);

  const onlyTotals = mainGranularity === zoomGranularity;
  try {
    if (api.zoomFetch) {
      api.zoomFetch.cancel();
    }
    api.zoomFetch = axios.CancelToken.source();

    yield all([
      put({
        type: types.SET_LOADER,
        loader: {
          isLoading: true,
        },
      }),
      put(clearCompareZoomData()),
    ]);

    let queries = yield call(parseQuery, {
      ...config,
      interval: config.zoomInterval,
      allMetrics: currentMetricsMeta,
    });

    if (onlyTotals) {
      queries = queries.map((query) => {
        set(query, 'queries.0.granularity', 'ALL');
        return query;
      });
    }

    yield put({
      type: types.SAVE_COMPARE_QUERIES,
      queries,
    });

    const calls = queries.map((query) =>
      call(getCompareFilterData, query, { cancelToken: api.zoomFetch.token }, http),
    );

    const response = yield all(calls);

    if (onlyTotals) {
      const zoomTotal = yield call(parseCompareTotals, response);

      zoomTotal.zoomin = true;

      yield put({
        type: types.UPDATE_ZOOM_TOTAL,
        totalObj: zoomTotal,
      });
    } else {
      const { features } = yield select(commonSelector);
      const zoomData = yield call(parseCompareData, config, response, features, currentMetricsMeta);

      yield put({
        type: types.FETCH_ZOOM_DATA_SUCCESS,
        data: zoomData,
      });
    }
  } catch (error) {
    if (error && error.code === 500) {
      yield put({
        type: types.SET_LOADER,
        loader: {
          isLoading: false,
          showError: true,
          errorMessage: error.message,
        },
      });
    } else {
      yield put({
        type: types.SET_LOADER,
        loader: {
          isLoading: false,
          showError: true,
          errorMessage: error.message,
        },
      });
    }
  } finally {
    if (yield cancelled()) {
      if (api.zoomFetch) api.zoomFetch.cancel();
    }
  }
}

function* fetchCompareData({ id, config, isZoom }) {
  if (!isZoom) {
    try {
      if (api.fetch) {
        api.fetch.cancel();
      }
      api.fetch = axios.CancelToken.source();

      yield put({
        type: types.SET_LOADER,
        loader: {
          isLoading: true,
        },
      });

      const queries = yield call(parseQuery, config);
      yield put({
        type: types.SAVE_COMPARE_QUERIES,
        queries,
      });

      const calls = queries.map((query) =>
        call(getCompareFilterData, query, { cancelToken: api.fetch.token }, http),
      );

      const response = yield all(calls);
      const { features } = yield select(commonSelector);
      const currentMetricsMeta = yield select(currentMetricsMetaSelector);

      const compareData = yield call(
        parseCompareData,
        config,
        response,
        features,
        currentMetricsMeta,
      );

      const { interval } = config;
      const mainGranularity = granularity(interval.from, interval.to, true);

      /* To identify which dashboard on the data */
      compareData.lastUpdatedDate = Date.now();
      compareData.id = id;
      compareData.granularity = mainGranularity;

      const actions = [
        put({
          type: types.FETCH_COMPARE_DATA_SUCCESS,
          data: compareData,
        }),
      ];

      const { payload } = yield select(sharingSelector);

      yield all(actions);
    } catch (error) {
      yield put({
        type: types.SET_LOADER,
        loader: {
          isLoading: false,
          showError: true,
          errorMessage: error.message,
        },
      });
    } finally {
      if (yield cancelled()) {
        if (api.fetch) api.fetch.cancel();
      }
    }
  } else {
    yield call(fetchCompareZoomData, config);
  }
}

function* clearModifyComparison() {
  yield all([
    put({
      type: types.SET_ACTIVE_EDIT_ID,
      activeEditId: null,
    }),
    put({
      type: commonTypes.SET_COMPARE_MODE,
      mode: NO_COMPARE_MODE,
    }),
  ]);
}

function* saveExportedMetric({ exportedMetric }) {
  yield put({
    type: types.SAVE_EXPORTED_METRIC,
    exportedMetric,
  });
}

function* watchFetchCompareData() {
  yield takeLatest(types.FETCH_COMPARE_DATA, fetchCompareData);
}

function* watchClearModifyComparison() {
  yield takeLatest(types.CLEAR_MODIFY_COMPARISON, clearModifyComparison);
}

function* watchExportedMetric() {
  yield takeLatest(types.EXPORT_COMPARE_METRIC, saveExportedMetric);
}

export default [
  watchFetchCompareData,
  watchClearModifyComparison,
  watchExportedMetric,
  ...fetchConfig,
  ...updateConfigSagas,
];
